import { useState, useEffect } from "react";
import { VITE_SUPABASE_ANON_KEY, VITE_SUPABASE_URL } from "@/utils/const"
import { motion } from "framer-motion";
import {
	DollarSign,
	TrendingUp,
	Calendar,
	Clock,
	User,
	Package,
	ArrowLeft,
	Download,
	Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface Transaction {
	id: string;
	total_amount: number;
	doula_amount: number;
	platform_commission: number;
	commission_percentage: number;
	status: string;
	created_at: string;
	parent_name: string;
	package_name: string;
}

interface EarningsSummary {
	total_earnings: number;
	this_month_earnings: number;
	pending_payments: number;
	completed_transactions: number;
	pending_transactions: number;
	average_commission: number;
	total_transactions: number;
}

export default function DoulaEarnings() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [transactions, setTransactions] = useState<Transaction[]>([]);
	const [earnings, setEarnings] = useState<EarningsSummary>({
		total_earnings: 0,
		this_month_earnings: 0,
		pending_payments: 0,
		completed_transactions: 0,
		pending_transactions: 0,
		average_commission: 0,
		total_transactions: 0,
	});
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");

	useEffect(() => {
		if (user) {
			loadEarnings();
		}
	}, [user]);

	const loadEarnings = async () => {
		if (!user) return;

		try {
			setLoading(true);

			// Get the public user ID (not auth user ID)
			const { data: publicUser, error: userError } = await supabase
				.from("users")
				.select("id")
				.eq("auth_user_id", user.id)
				.single();

			if (userError || !publicUser) {
				console.error("Error fetching public user:", userError);
				return;
			}

			const doulaUserId = publicUser.id;

			// Use the deployed edge function
			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/get-doula-earnings`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						doulaUserId: doulaUserId,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				console.error("Error loading earnings:", result.error);
				return;
			}

			setEarnings(result.data.summary || earnings);
			setTransactions(result.data.transactions || []);
		} catch (error) {
			console.error("Error loading earnings:", error);
		} finally {
			setLoading(false);
		}
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-green-100 text-green-800";
			case "pending":
				return "bg-yellow-100 text-yellow-800";
			case "failed":
				return "bg-red-100 text-red-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "completed":
				return "✅";
			case "pending":
				return "⏳";
			case "failed":
				return "❌";
			default:
				return "❓";
		}
	};

	const filteredTransactions = transactions.filter((transaction) => {
		const matchesSearch =
			transaction.parent_name
				.toLowerCase()
				.includes(searchTerm.toLowerCase()) ||
			transaction.package_name.toLowerCase().includes(searchTerm.toLowerCase());

		const matchesStatus =
			statusFilter === "all" || transaction.status === statusFilter;

		return matchesSearch && matchesStatus;
	});

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100 p-6">
				<div className="max-w-7xl mx-auto">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						className="text-center py-20"
					>
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
						<p className="text-gray-600 font-karla">Loading your earnings...</p>
					</motion.div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100">
			<div className="max-w-7xl mx-auto p-6">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					className="mb-8"
				>
					<div className="flex items-center mb-6">
						<Button
							onClick={() => navigate("/doula-dashboard")}
							className="mr-4 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow font-karla"
						>
							<ArrowLeft className="h-5 w-5 mr-2" />
							Back to Dashboard
						</Button>
					</div>

					<div className="text-center mb-8">
						<h1 className="text-5xl leading-10 font-light text-gray-900 mb-4 font-abc-prophet">
							Earnings &amp; Transactions
						</h1>
						<p className="text-xl lg:text-[24px] text-black font-karla lg:leading-9 max-w-2xl mx-auto">
							Track your earnings, view transaction history, and monitor your
							income from Gaia
						</p>
					</div>
				</motion.div>

				{/* Earnings Overview Cards */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1 }}
					className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
				>
					{/* Total Earnings */}
					<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
						<CardContent className="p-6">
							<div className="flex items-center justify-between">
								<div>
									<h5 className="text-sm font-medium text-black font-karla">
										Total Earnings
									</h5>
									<p className="text-2xl font-bold text-black font-karla">
										${earnings.total_earnings.toLocaleString()}
									</p>
								</div>
								<DollarSign className="h-8 w-8 text-newTheme-orange" />
							</div>
						</CardContent>
					</Card>

					{/* This Month's Earnings */}
					<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
						<CardContent className="p-6">
							<div className="flex items-center justify-between">
								<div>
									<h5 className="text-sm font-medium text-black font-karla">
										This Month
									</h5>
									<p className="text-2xl font-bold text-black font-karla">
										${earnings.this_month_earnings.toLocaleString()}
									</p>
								</div>
								<TrendingUp className="h-8 w-8 text-newTheme-spruce" />
							</div>
						</CardContent>
					</Card>

					{/* Pending Payments */}
					<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
						<CardContent className="p-6">
							<div className="flex items-center justify-between">
								<div>
									<h5 className="text-sm font-medium text-black font-karla">
										Pending
									</h5>
									<p className="text-2xl font-bold text-black font-karla">
										${earnings.pending_payments.toLocaleString()}
									</p>
								</div>
								<Clock className="h-8 w-8 text-newTheme-orange" />
							</div>
						</CardContent>
					</Card>

					{/* Total Transactions */}
					<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
						<CardContent className="p-6">
							<div className="flex items-center justify-between">
								<div>
									<h5 className="text-sm font-medium text-black font-karla">
										Transactions
									</h5>
									<p className="text-2xl font-bold text-black font-karla">
										{earnings.total_transactions}
									</p>
								</div>
								<Calendar className="h-8 w-8 text-newTheme-spruce" />
							</div>
						</CardContent>
					</Card>
				</motion.div>

				{/* Commission Breakdown */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.2 }}
					className="mb-8"
				>
					<Card className="glass-card border-0 shadow-lg">
						<CardHeader>
							<CardTitle className="text-xl font-karla flex items-center">
								<DollarSign className="h-5 w-5 mr-2 text-primary-600" />
								Commission Breakdown
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
								<div className="text-center p-4 bg-primary-50 rounded-lg">
									<h5 className="text-sm text-black font-karla">
										Your Earnings (90%)
									</h5>
									<p className="text-2xl font-bold text-primary-900 font-karla">
										${earnings.total_earnings.toLocaleString()}
									</p>
								</div>
								<div className="text-center p-4 bg-primary-50 rounded-lg">
									<h5 className="text-sm text-black font-karla">
										Platform Commission (10%)
									</h5>
									<p className="text-2xl font-bold text-primary-900 font-karla">
										${(earnings.total_earnings * 0.111).toLocaleString()}
									</p>
								</div>
								<div className="text-center p-4 bg-primary-50 rounded-lg">
									<h5 className="text-sm text-black font-karla">
										Average Commission
									</h5>
									<p className="text-2xl font-bold text-primary-900 font-karla">
										${earnings.average_commission.toFixed(2)}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</motion.div>

				{/* Transactions Section */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.3 }}
				>
					<Card className="glass-card border-0 shadow-lg">
						<CardHeader>
							<div className="flex items-center justify-between">
								<CardTitle className="text-xl font-karla flex items-center">
									<Calendar className="h-5 w-5 mr-2 text-primary-600" />
									Transaction History
								</CardTitle>
								<Button
									variant="outline"
									size="sm"
									className="bg-white/80 hover:bg-white"
								>
									<Download className="h-4 w-4 mr-2" />
									Export
								</Button>
							</div>
						</CardHeader>
						<CardContent>
							{/* Search and Filters */}
							<div className="mb-6 space-y-4">
								<div className="flex flex-col md:flex-row gap-4">
									<div className="relative flex-1">
										<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
										<Input
											placeholder="Search by parent name or package..."
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
											className="pl-10"
										/>
									</div>
									<select
										value={statusFilter}
										onChange={(e) => setStatusFilter(e.target.value)}
										className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
									>
										<option value="all">All Status</option>
										<option value="completed">Completed</option>
										<option value="pending">Pending</option>
										<option value="failed">Failed</option>
									</select>
								</div>
							</div>

							{/* Transactions List */}
							<div className="space-y-4">
								{filteredTransactions.length === 0 ? (
									<div className="text-center py-12">
										<DollarSign className="h-16 w-16 text-newTheme-orange mx-auto mb-4" />
										<h3 className="text-lg font-medium text-gray-900 mb-2 font-karla">
											No transactions found
										</h3>
										<p className="text-gray-600 font-karla">
											{searchTerm || statusFilter !== "all"
												? "Try adjusting your search or filters."
												: "You haven't received any payments yet."}
										</p>
									</div>
								) : (
									filteredTransactions.map((transaction, index) => (
										<motion.div
											key={transaction.id}
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.1 * index }}
											className="p-6 bg-white/80 rounded-lg border border-gray-200 hover:border-primary-300 transition-colors"
										>
											<div className="flex items-center justify-between">
												<div className="flex items-center space-x-4">
													<div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
														<User className="h-6 w-6 text-primary-600" />
													</div>
													<div>
														<h4 className="font-semibold text-gray-900 font-karla">
															{transaction.parent_name}
														</h4>
														<p className="text-sm text-gray-600 font-karla flex items-center">
															<Package className="h-4 w-4 mr-1" />
															{transaction.package_name}
														</p>
														<p className="text-xs text-gray-500 font-karla">
															{formatDate(transaction.created_at)}
														</p>
													</div>
												</div>

												<div className="text-right">
													<div className="flex items-center space-x-3">
														<div>
															<p className="text-lg font-bold text-gray-900 font-karla">
																${transaction.doula_amount.toLocaleString()}
															</p>
															<p className="text-xs text-gray-500 font-karla">
																Commission: $
																{transaction.platform_commission.toLocaleString()}
															</p>
														</div>
														<Badge
															variant="outline"
															className={`${getStatusColor(transaction.status)}`}
														>
															<span className="mr-1">
																{getStatusIcon(transaction.status)}
															</span>
															{transaction.status}
														</Badge>
													</div>
												</div>
											</div>
										</motion.div>
									))
								)}
							</div>
						</CardContent>
					</Card>
				</motion.div>
			</div>
		</div>
	);
}
